import type { CustomLogo } from '@/models/CustomLogo';

/**
 * Which of an order line's custom logos the customer actually saw — the
 * builder's rule (`isLogoVisibleForDesign`, Clone of builder v2
 * `src/lib/logoScope.ts`):
 *  - on a customer's own design (uploaded / style-transferred / edited and
 *    saved as theirs), only logos scoped to THAT design show;
 *  - on a catalog template, only template-context logos (no scope) show.
 *
 * The order keeps every logo the customer ever placed on the product, in
 * either context. Order 28404 carried a "Team Logo" from earlier designing on
 * the template (no scope) next to the logos of the custom design it was
 * ordered with; the builder hid it, the production file printed it on top.
 */
export interface LogoScopeProduct {
  design_id?: number | string | null;
  production_design_url?: string | null;
  custom_logos?: CustomLogo[] | null;
}

/** The backend files customers' own designs under `files/custom_design/{customer}`. */
const CUSTOMER_DESIGN_PATH = /(^|\/)files\/custom_design\//;

/** Whether the order line's design is a customer's own design. */
export function isCustomerDesign(product: LogoScopeProduct): boolean {
  const designId = Number(product.design_id);
  const scopedHere =
    Number.isFinite(designId) &&
    designId > 0 &&
    (product.custom_logos ?? []).some((l) => Number(l?.scope_design_id) === designId);
  return scopedHere || CUSTOMER_DESIGN_PATH.test(product.production_design_url ?? '');
}

export function isLogoVisibleForProduct(logo: CustomLogo, product: LogoScopeProduct): boolean {
  const scope = logo.scope_design_id ?? null;
  const customerDesign = isCustomerDesign(product);
  if (scope === null || scope === '') return !customerDesign;
  return customerDesign && Number(scope) === Number(product.design_id);
}

/** The logos to print / inspect for this order line, in their original order. */
export function visibleCustomLogos(product: LogoScopeProduct): CustomLogo[] {
  return (product.custom_logos ?? []).filter((l) => !!l && isLogoVisibleForProduct(l, product));
}
